/* How many pages, guessed from the bytes, before anything is uploaded.
 *
 * This exists only to put a number on the wait. The server is the authority on
 * the page count -- it opens the document properly -- but its answer arrives
 * at the same moment as the finished pages, which is too late to tell somebody
 * how long to expect to wait.
 *
 * So this reads the raw bytes and looks for the page tree's /Count. It is a
 * GUESS and is treated as one everywhere it is used: a wrong number makes an
 * estimate slightly wrong, and zero makes the estimate disappear rather than
 * become nonsense. Nothing depends on it being right.
 *
 * It is deliberately bounded. A 50 MB scan would otherwise be pulled into
 * memory in full just to run a regular expression over it; the page tree sits
 * near the catalogue or near the trailer, so half a megabyte from each end
 * finds it in every file measured -- including ones written with compressed
 * cross-reference streams.
 *
 * Where it will fail: a document whose page tree lives inside a compressed
 * object stream, which is legal and not rare. That returns 0, the estimate is
 * withheld, and the spinner carries the wait on its own. That is the correct
 * outcome, not a bug to work around.
 */

// Half a megabyte from each end. Enough in every file measured, and small
// enough that reading it costs nothing worth noticing.
const WINDOW = 512 * 1024

// Beyond this the guess is not credible -- a /Count that large is almost
// certainly something else that happened to match.
const MAX_CREDIBLE = 5000

const COUNT = /\/Count\s+(\d+)/g
// /Type /Page but NOT /Type /Pages, which is the tree node rather than a leaf
const PAGE = /\/Type\s*\/Page[^s]/g

/* The bytes worth scanning: the head, and the tail if the file is big enough
 * that they do not overlap. */
export async function sample(file) {
    const size = file.size
    if (!size) return ""
    const head = await file.slice(0, Math.min(WINDOW, size)).text()
    if (size <= WINDOW * 2) return head
    return head + (await file.slice(size - WINDOW, size).text())
}

/* Pages from a sampled string, or 0 when it cannot be told. */
export function countFrom(text) {
    if (!text) return 0

    // The page tree's root node carries the total, and nested nodes carry
    // their own subtotals, so the largest is the one wanted.
    let best = 0
    COUNT.lastIndex = 0
    let m
    while ((m = COUNT.exec(text)) !== null) {
        const n = parseInt(m[1], 10)
        if (n > best) best = n
    }
    if (best > 0 && best <= MAX_CREDIBLE) return best

    // No /Count in reach: fall back to counting leaves. Only right when the
    // whole document was sampled, so it under-counts a large file rather than
    // over-counting -- which errs towards a shorter estimate, not a longer one.
    PAGE.lastIndex = 0
    const leaves = (text.match(PAGE) || []).length
    return leaves > 0 && leaves <= MAX_CREDIBLE ? leaves : 0
}

export async function guessPageCount(file) {
    try {
        return countFrom(await sample(file))
    } catch (err) {
        return 0
    }
}

/* What a page actually costs between the upload finishing and the response
 * arriving -- which is the phase this number has to describe.
 *
 * It was 55, taken from the rasterising alone: PyMuPDF needs 41 ms a page at
 * 150 dpi. That was the wrong measurement. Rendering is the small half; the
 * server then uploads each page to S3 one at a time, and that dominates.
 * Timed against production with a small file so the upload of the PDF itself
 * was negligible, thirty pages took 5.8 and 6.2 seconds -- close to 200 ms a
 * page, not 55.
 *
 * Rounded up rather than to the mean, because an estimate that runs over reads
 * as a fault and one that comes in early does not.
 *
 * The sequential upload is the thing worth fixing, not this constant. Doing
 * those in parallel would cut the wait several times over, and then this
 * number should be measured again. */
export const MS_PER_PAGE = 210

export function renderEstimateMs(pages) {
    return pages > 0 ? pages * MS_PER_PAGE : 0
}
